import { FC, ReactElement } from "react";
import { createPortal } from "react-dom";
import { Draggable } from "@hello-pangea/dnd";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DragDropVerticalIcon,
  SquareLock01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Checkbox, CheckboxSize } from "shared/components";
import type { ColumnVisibilityItem } from "providers/Auth";

type ColumnRowProps = {
  column: ColumnVisibilityItem;
  index: number;
  draggableId: string;
  isDragDisabled: boolean;
  /** Rendered outside the drop areas: the column is fixed to an edge of the table. */
  isFixed?: boolean;
  onToggle: (id: string) => void;
};

const ROW_CLASS =
  "group flex items-center gap-3 px-4 h-10 border-b border-alpha-100";

export const ColumnRow: FC<ColumnRowProps> = ({
  column,
  index,
  draggableId,
  isDragDisabled,
  isFixed = false,
  onToggle
}) => {
  // always_on columns are pinned: rendered statically (outside the drag area)
  // so they can't be moved and nothing can be dropped above them, mirroring the
  // pinned-left columns in the table.
  if (column.always_on)
    return (
      <div className={`${ROW_CLASS} hover:bg-alpha-20`}>
        <HugeiconsIcon
          icon={SquareLock01Icon}
          size={16}
          strokeWidth={2}
          className="shrink-0 text-content-300"
        />
        <span className="text-sm font-medium text-content-400 flex-1 truncate">
          {column.label}
        </span>
      </div>
    );

  // A column fixed to the table's edge can be hidden but not moved.
  if (isFixed)
    return (
      <div className={`${ROW_CLASS} hover:bg-alpha-20`}>
        <Checkbox
          size={CheckboxSize.SM}
          checked={column.checked}
          onChange={() => onToggle(column.id)}
          wrapperClassName="!w-auto shrink-0 !cursor-pointer [&_*]:!cursor-pointer"
        />
        <span className="text-sm font-medium text-content-700 flex-1 min-w-0 truncate">
          {column.label}
        </span>
      </div>
    );

  const draggable = !isDragDisabled;

  return (
    <Draggable
      draggableId={draggableId}
      index={index}
      isDragDisabled={!draggable}
    >
      {(provided, snapshot) => {
        const row: ReactElement = (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...(draggable ? provided.dragHandleProps : {})}
            className={`${ROW_CLASS} ${
              snapshot.isDragging
                ? "bg-alpha-40 shadow-sm cursor-grabbing [&_*]:cursor-grabbing"
                : "hover:bg-alpha-20"
            } ${
              draggable
                ? "cursor-grab active:cursor-grabbing [&_*]:cursor-grab active:[&_*]:cursor-grabbing"
                : ""
            }`}
          >
            {/* Only the box toggles; the label is a plain span so clicking the
                row body doesn't hide the column. */}
            <Checkbox
              size={CheckboxSize.SM}
              checked={column.checked}
              onChange={() => onToggle(column.id)}
              wrapperClassName="!w-auto shrink-0 !cursor-pointer [&_*]:!cursor-pointer"
            />
            <span className="text-sm font-medium text-content-700 flex-1 min-w-0 truncate">
              {column.label}
            </span>

            {draggable ? (
              <HugeiconsIcon
                icon={DragDropVerticalIcon}
                size={16}
                strokeWidth={2}
                className={`shrink-0 text-content-400 ml-auto ${
                  snapshot.isDragging
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              />
            ) : null}
          </div>
        );

        // Portal the dragged clone to body so it escapes the popover's
        // transformed parent (@floating-ui uses transform: translate(...) for
        // positioning, which breaks position: fixed on the drag clone).
        if (snapshot.isDragging) return createPortal(row, document.body);
        return row;
      }}
    </Draggable>
  );
};
